import { Mark } from '@tiptap/core'

export const SUGGESTION_MARK_NAME = 'suggestion'

export const SUGGESTION_TYPE_INSERTION = 'insertion'
export const SUGGESTION_TYPE_DELETION = 'deletion'
export const SUGGESTION_TYPE_FORMATTING = 'formatting'

export type SuggestionType = typeof SUGGESTION_TYPE_INSERTION | typeof SUGGESTION_TYPE_DELETION | typeof SUGGESTION_TYPE_FORMATTING

export const LIST_TYPE_BULLET = 'bulletList'
export const LIST_TYPE_ORDERED = 'orderedList'
export const LIST_ITEM_NODE_NAME = 'listItem'

export type ListType = typeof LIST_TYPE_BULLET | typeof LIST_TYPE_ORDERED

export type ListIndentSource = {
  // Slot in the source parent list where the listItem used to sit.
  position: number
  // Set when the lift removed the (now-empty) parent list — the reject recreates
  // a list of this type+attrs around the listItem before placing it back.
  wrap?: { type: ListType; attrs: Record<string, unknown> }
}

export type ListChange =
  | { kind: 'wrap'; listType: ListType }
  | { kind: 'unwrap'; listType: ListType }
  | { kind: 'changeListType'; from: ListType; to: ListType }
  | { kind: 'indent'; delta: number; source?: ListIndentSource }

export type FormattingChanges = {
  added: string[]
  removed: string[]
  listChanges?: ListChange[]
}

export type SuggestionBaseAttrs = {
  suggestionId: string
  authorId: number
  authorName: string
  createdAt: string
}

export type SuggestionAttrs = {
  suggestionId: string | null
  type: SuggestionType | null
  authorId: number | null
  authorName: string | null
  createdAt: string | null
  formattingChanges: FormattingChanges | null
}

type DataAttributeOptions = {
  parse?: (value: string) => unknown
  serialize?: (value: any) => string
}

function dataAttribute(attributeKey: string, datasetKey: string, options: DataAttributeOptions = {}) {
  const parse = options.parse ?? ((value: string) => value)
  const serialize = options.serialize ?? String
  return {
    default: null,
    parseHTML: (element: HTMLElement) => {
      const value = element.getAttribute(datasetKey)
      return value === null ? null : parse(value)
    },
    renderHTML: (attrs: Record<string, any>) => (attrs[attributeKey] === null ? {} : { [datasetKey]: serialize(attrs[attributeKey]) }),
  }
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

export const SuggestionMark = Mark.create({
  name: SUGGESTION_MARK_NAME,

  inclusive: false,

  excludes: '',

  addAttributes() {
    return {
      suggestionId: dataAttribute('suggestionId', 'data-suggestion-id'),
      type: dataAttribute('type', 'data-suggestion-type'),
      authorId: dataAttribute('authorId', 'data-suggestion-author-id', { parse: Number }),
      authorName: dataAttribute('authorName', 'data-suggestion-author-name'),
      createdAt: dataAttribute('createdAt', 'data-suggestion-created-at'),
      formattingChanges: dataAttribute('formattingChanges', 'data-suggestion-formatting-changes', { parse: safeJsonParse, serialize: JSON.stringify }),
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-suggestion-id]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const type = HTMLAttributes['data-suggestion-type'] ?? null
    const className =
      type === SUGGESTION_TYPE_INSERTION
        ? 'suggestion suggestion-insertion'
        : type === SUGGESTION_TYPE_DELETION
          ? 'suggestion suggestion-deletion'
          : type === SUGGESTION_TYPE_FORMATTING
            ? 'suggestion suggestion-formatting'
            : 'suggestion'
    return ['span', { ...HTMLAttributes, class: className }, 0]
  },
})
